import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { ButtonEdit } from '../../../general/mini_components';
import { Button, Card, Typography, Grid, IconButton } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import styled from 'styled-components';
import { Alert } from "@mui/lab";
import { Translate, withLocalize } from 'react-localize-redux';
import File from '../../../general/File';
import SmartField from '../../../general/SmartFields';
import { extractBmiValue } from '../../../general/SmartFields/normaliseInitialListElements';
import { fullDateFromPostgresString, getData, isSmartField, localStringDateFromTimeStamp, stringDatePostgresToDate } from '../../../../utils/index.jsx';
import { ALL_SMARTFIELDS_TYPES, MEDICAL_HISTORY_FIELDS } from '../../../../constants';
import { PatientStatus } from '../../../../constants/types';
import Multioption from '../../../general/Multioption';
import { PERMISSION } from '../../share/user_roles';

/**
 * Component that shows all the records/submissions of a section of a patient in a survey
 */


const CardPadding = styled(Card)`
    padding:1rem;
    margin-bottom:1rem;
`;
const GridPadded = styled(Grid)`
    padding:${props => (props.padding ? '0rem 0rem 1rem 1rem' : '0rem')};
`;
const HeaderSection = styled.div`
    display:flex;

`;
function ShowRecordsSection(props) {
    //let [indexSubmission, setIndexSubmission ] = useState(0);
    let [error, setError] = useState(false);
    const { uuidPatient } = useParams();
    const currentInvestigation = useSelector((state) => state.investigations?.currentInvestigation);
    const patients = useSelector((state) => currentInvestigation ? state.patients?.data?.[currentInvestigation.uuid] : null);
    const currentResearcherUuid = useSelector((state) => state.profile?.info?.uuid);
    const isDeceased = useMemo(() => {
        if (!patients || !uuidPatient) return false;
        const patient = patients.find(p => p.uuid === uuidPatient);
        return patient?.status === PatientStatus.DECEASED;
    }, [patients, uuidPatient]);
    // SHER-223: only the original author of a submission may edit it. We hide
    // the pencil for everyone else; the API also enforces this with a 403, so
    // a stale client cannot bypass the gate. If `uuidResearcher` is missing
    // (legacy submission with no author recorded) we fall back to allowing
    // the edit so we don't lock historical notes out entirely.
    const isAuthor = !props.uuidResearcher || props.uuidResearcher === currentResearcherUuid;

    // SHER-223 Phase 2: a clinical lead with EDIT_OTHER_AUTHOR_SUBMISSION can
    // bypass the author gate via "Edit anyway"; the API still demands
    // editReason + password regardless of submission age. The "Add correction"
    // entry point and the addenda trail itself live on the parent (per
    // submission, not per section) — see ShowSingleSubmissionPatient.
    const permissions = props.permissions || [];
    const canOverrideEdit = permissions.includes(PERMISSION.EDIT_OTHER_AUTHOR_SUBMISSION);
    const showOverrideEditButton = !props.printMode && !isDeceased && !isAuthor && canOverrideEdit;

    function editSection(uuidSection){

        props.callBackEditSubmission(props.idSubmission, uuidSection);
    }

    function renderValue(valueRecord, field, hideField = false){
        const showField = props.section.fields.length > 1 && !hideField;
        if(!valueRecord || !valueRecord.value){
            return(
            <Grid item paddingLeft={3}>
                {
                    showField &&
                    <Typography variant="h6" color="textPrimary">
                        {field.name}: 
                    </Typography>
                }
                {
                    ALL_SMARTFIELDS_TYPES.includes(field.type) &&
                    <Typography variant="body2">
                        <Translate id="general.no" />
                    </Typography>
                }
                {
                    !ALL_SMARTFIELDS_TYPES.includes(field.type) &&
                    "-"
                }
            </Grid>)
        }
        if(field.type === "textarea" || MEDICAL_HISTORY_FIELDS.includes(field.type)){
            let value;
            if(field.type === "textarea" || field.type === "medical_history_template"){
                value = valueRecord.value || '';
            }
            else{
            
                // For medical history fields, the value is stored in an array with medical_history_ai property
                value = valueRecord.value && Array.isArray(valueRecord.value) && valueRecord.value.length > 0 
                    ? valueRecord.value[0].medical_history_ai || valueRecord.value[0]['template_medical_history-notes'] || ''
                    : '';
            }
            
            //const parsedValue = value.replace('<h1>','').replace('</h1>','').replace('<h2>','').replace('</h2>','');;
            const StyledDiv = styled.div`
                * {
                    white-space: normal
                }
                
                &.ql-editor p {
                    margin-bottom: 0.25em;
                    line-height: 1.2;
                }
            `;
            return(
            <Grid item paddingLeft={3}>
                {
                    showField &&
                    <Typography variant="h6" color="textPrimary" fontWeight='800'>
                        {field.name}: 
                    </Typography>
                }
                <Typography variant="body2">
                    <StyledDiv className='ql-editor' dangerouslySetInnerHTML={{__html: value || "-"}}></StyledDiv>                    
                </Typography>
            </Grid>
            );
        }
        else if(valueRecord.surveyField.type === "file"){
            return <File key={valueRecord.id} mode="show" value={valueRecord.value}
                    label={field.name}  />
        }
        else if(valueRecord.surveyField.type === "appointment"){
            const dateAppointment = stringDatePostgresToDate(valueRecord.value);
            return (
                <Grid item paddingLeft={3}>
                <Typography variant="h6" color="textPrimary">
                    <Translate id="general.date" />: {dateAppointment.toLocaleString(props.activeLanguage.code,{
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                        })}
                </Typography>
                </Grid>
            )
        }
        else if(valueRecord.surveyField.type === "multioption"){
            return (<Multioption mode="show" label= {field.name} options={field.options} value={valueRecord.value} />);
        }
        else if(valueRecord.surveyField.type === "bmi"){
            // SHER-210: BMI is a single computed value, not a list. Routing it through
            // SmartField hits the table-rendering branch with the object's keys as headers,
            // and a saved object value (vs array) renders an empty Card. Display stacked.
            //
            // SHER-210 (Pedro 2026-05-04 feedback): stack the fields vertically (IMC, height,
            // weight) per CHOM's existing layout. Uses the central extractBmiValue helper.
            const view = extractBmiValue(valueRecord.value);
            const bmiText = view.bmi != null ? view.bmi.toFixed(2) : '—';
            return(
                <Grid item paddingLeft={3}>
                    { showField &&
                        <Typography variant="h6" color="textPrimary">
                            {field.name}:
                        </Typography>
                    }
                    <Typography variant="body2"><b>IMC:</b> {bmiText}</Typography>
                    {view.height != null && (
                        <Typography variant="body2"><b><Translate id="hospital.bmi-height" />:</b> {view.height} cm</Typography>
                    )}
                    {view.weight != null && (
                        <Typography variant="body2"><b><Translate id="hospital.bmi-weight" />:</b> {view.weight} kg</Typography>
                    )}
                </Grid>
            );
        }
        else if(isSmartField(valueRecord.surveyField.type)){
            return(
                <Grid item paddingLeft={3}>
                    <SmartField type={valueRecord.surveyField.type} mode="show"
                        initialState={{listElements:valueRecord.value}} printMode={props.printMode}
                        label={valueRecord.surveyField.label}  />
                </Grid>)
        }
        else if(typeof valueRecord.value.getMonth === 'function' ){
            return(
                <Grid item paddingLeft={3}>
                { showField &&
                    <Typography variant="h6" color="textPrimary">
                        {field.name}: 
                    </Typography>
                }
                <Typography variant="body2" gutterBottom>
                    { valueRecord.value.toISOString() }
                </Typography>
            </Grid>
            );
        }   
        else if(valueRecord.surveyField.type === "date"){
            
            return (
                <Grid item paddingLeft={3}>
                    { showField &&
                        <Typography variant="h6" color="textPrimary">
                            {field.name}: 
                        </Typography>
                    }
                    <Typography variant="body2" gutterBottom>
                        { localStringDateFromTimeStamp( props.activeLanguage.code, valueRecord.value) }
                    </Typography>
                </Grid>   
            )
        }
        else if(valueRecord.surveyField.type === "disposition"){
            let val = valueRecord.value;
            // Value may be a JSON string from the DB — parse it
            if(typeof val === 'string' && val.startsWith('{')){
                try { val = JSON.parse(val); } catch(e) { /* keep as string */ }
            }
            const isObj = typeof val === 'object' && val !== null;
            const disposition = isObj ? val.disposition : val;
            const dateOfDeath = isObj ? val.dateOfDeath : null;
            const immediateCause = isObj ? (val.immediateCause || val.cause) : null;
            const underlyingCause = isObj ? val.underlyingCause : null;
            const placeOfDeath = isObj ? val.placeOfDeath : null;
            const deathConfirmedBy = isObj ? val.deathConfirmedBy : null;
            const palliativePathway = isObj ? val.palliativePathway : null;
            return(
                <Grid item paddingLeft={3}>
                { showField &&
                    <Typography variant="h6" color="textPrimary">
                        {field.name}:
                    </Typography>
                }
                <Typography variant="body2" gutterBottom>
                    <Translate id={`patient.disposition.${(disposition || '').toLowerCase().replace('_','-')}`} />
                </Typography>
                {
                    disposition === "DECEASED" && dateOfDeath &&
                    <Typography variant="body2" gutterBottom>
                        <strong><Translate id="patient.death.date" />:</strong> {fullDateFromPostgresString(dateOfDeath)}
                    </Typography>
                }
                {
                    disposition === "DECEASED" && immediateCause &&
                    <Typography variant="body2" gutterBottom>
                        <strong><Translate id="patient.death.immediate-cause" />:</strong> {immediateCause}
                    </Typography>
                }
                {
                    disposition === "DECEASED" && underlyingCause &&
                    <Typography variant="body2" gutterBottom>
                        <strong><Translate id="patient.death.underlying-cause" />:</strong> {underlyingCause}
                    </Typography>
                }
                {
                    disposition === "DECEASED" && placeOfDeath &&
                    <Typography variant="body2" gutterBottom>
                        <strong><Translate id="patient.death.place" />:</strong> <Translate id={`patient.death.place-${placeOfDeath.toLowerCase()}`} />
                    </Typography>
                }
                {
                    disposition === "DECEASED" && deathConfirmedBy &&
                    <Typography variant="body2" gutterBottom>
                        <strong><Translate id="patient.death.confirmed-by" />:</strong> <Translate id={`patient.death.confirmed-by-${deathConfirmedBy.toLowerCase()}`} />
                    </Typography>
                }
                {
                    disposition === "DECEASED" && palliativePathway !== null && palliativePathway !== undefined &&
                    <Typography variant="body2" gutterBottom>
                        <strong><Translate id="patient.death.palliative-pathway" />:</strong> {palliativePathway ? <Translate id="general.yes" /> : <Translate id="general.no" />}
                    </Typography>
                }
            </Grid>
            );
        }
        else{
            // SHER-208: never let an object array reach a JSX text slot.
            // Legacy smartfield records (e.g. stored type 'ict' that doesn't match the
            // current SMARTFIELD_TYPE.TYPE_ICT='icd11' enum, so isSmartField() above
            // returns false) fall through here with an array of objects as the value.
            // Without this guard React throws #31 ("Objects are not valid as a React child")
            // and the entire section is replaced by the ErrorBoundary's "Something went wrong".
            //
            // For the common smartfield shape — array of objects with a name-like key
            // and a "<prefix>-code" key (ICT, ICD-11, background, allergy, treatment) —
            // format each item as "Name (CODE)" so the user actually reads the data.
            const formatSmartfieldItem = (item) => {
                if (item == null) return '';
                if (typeof item !== 'object') return String(item);
                const entries = Object.entries(item).filter(([k]) => k !== 'postId' && k !== 'treatmentPostId');
                const codeEntry = entries.find(([k, v]) => k.endsWith('-code') && v != null && v !== '');
                const nameEntry = entries.find(([k, v]) =>
                    !k.endsWith('-code') && typeof v === 'string' && v.trim() !== ''
                );
                const name = nameEntry ? String(nameEntry[1]).trim() : '';
                const code = codeEntry ? String(codeEntry[1]).trim() : '';
                if (name && code) return `${name} (${code})`;
                if (name) return name;
                if (code) return code;
                try { return JSON.stringify(item); } catch (_) { return String(item); }
            };
            const safeValue = (() => {
                const v = valueRecord.value;
                if (v == null) return '';
                if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return v;
                if (Array.isArray(v)) {
                    return v.map(formatSmartfieldItem).filter((s) => s).join(', ');
                }
                if (typeof v === 'object') return formatSmartfieldItem(v);
                try { return JSON.stringify(v); } catch (_) { return String(v); }
            })();
            return(
                <Grid item paddingLeft={3}>
                { showField &&
                    <Typography variant="h6" color="textPrimary">
                        {field.name}:
                    </Typography>
                }
                <Typography variant="body2" gutterBottom>
                    { safeValue }
                </Typography>
            </Grid>
            );
        }
    }
   
    
    function renderSubmission(){
        if(props.records.length === 0){
            return (
                <GridPadded container padding={!props.printMode}>
                    <Grid item xs={12}>
                        <Grid item paddingLeft={3}>
                            <Typography variant="body2" gutterBottom>
                                No records available
                            </Typography>
                        </Grid>
                    </Grid>
                </GridPadded>
            )   
        }
        else{
            // SHER-208 + SHER-243: iterate the records themselves, not
            // section.fields. Each record's surveyField metadata is
            // embedded by the API (name, type, label, order), so it carries
            // everything renderValue() needs — including the original
            // labels for orphan records (fields the EDC editor later
            // soft-deleted).
            //
            // The earlier implementation iterated [section.fields ∪
            // orphanFields] and looked up a record for each. That had two
            // problems for historical submissions:
            //   - Fields ADDED to the template after the submission was
            //     saved had no record, so renderValue() rendered them as
            //     a literal "-" placeholder — the stray dash above the
            //     preserved content Pedro flagged on 2026-05-22 (SHER-243).
            //   - The read view diverged from SubmissionPDFDocument, which
            //     already iterates submission.surveyRecords directly
            //     (SHER-246). Same contract here.
            //
            // Two safety nets in renderValue() / Multioption keep this from
            // crashing for legacy data:
            //   1. The default branch of renderValue() coerces non-primitive
            //      values to a string before they hit JSX, so legacy
            //      smartfield arrays (e.g. records stored with the old 'ict'
            //      type) can't trip React #31.
            //   2. <Multioption mode="show"> tolerates a missing options
            //      array (orphan fields don't carry options) and falls back
            //      to rendering the recorded values directly.
            const recordsWithField = props.records.filter(r => r.surveyField);
            return(
                <GridPadded container padding={!props.printMode} direction="column" spacing={3}>
                    {
                        [...recordsWithField]
                            .sort((a, b) => (a.surveyField.order ?? 0) - (b.surveyField.order ?? 0))
                            .map(record => (
                                <Grid item xs={12} key={record.id}>
                                    {
                                        renderValue(
                                            record,
                                            record.surveyField,
                                            record.surveyField.name === props.section.name,
                                        )
                                    }
                                </Grid>
                            ))
                    }
                </GridPadded>
            );
        }
        
    }
    if(!props.records || error){
        return (
            <Alert mb={4} severity="error">
                <Translate id="investigation.share.error.description" />
            </Alert>
        );
    }
    else{
        const dateCreated = new Date(props.updatedAt);
        return (
            <CardPadding padding={props.printMode}>
                <Grid container direction="column" spacing={3}>
                    <Grid item>
                        {
                            !props.noTitle &&
                            <HeaderSection>
                                <Typography variant="subtitle1" color="textPrimary" style={{ fontWeight: 600 }}>
                                    { props.section.name }
                                </Typography>
                                {
                                    (!props.printMode && props.onPrintSubmission) &&
                                    <IconButton 
                                        onClick={() => props.onPrintSubmission()}
                                        color="primary"
                                        size="small"
                                        sx={{
                                            marginLeft: 1
                                        }}
                                    >
                                        <PrintIcon />
                                    </IconButton>
                                }
                                {
                                    !props.printMode && !isDeceased && isAuthor &&
                                    <ButtonEdit color="primary" onClick={() => editSection(props.section.uuid)} />
                                }
                                {
                                    // SHER-223 Phase 2: clinical lead with the override permission
                                    // bypasses the author gate. Reuses the same edit flow but the
                                    // API will demand reason + password regardless of submission age.
                                    showOverrideEditButton &&
                                    <Button
                                        size="small"
                                        color="warning"
                                        variant="text"
                                        data-testid="edit-override-button"
                                        onClick={() => editSection(props.section.uuid)}
                                        sx={{ marginLeft: 1 }}
                                    >
                                        <Translate id="hospital.submission-edit-override-button" />
                                    </Button>
                                }
                            </HeaderSection>
                        }

                    </Grid>
                    {
                        renderSubmission()
                    }

                </Grid>
            </CardPadding>
        )
    }

}

ShowRecordsSection.propTypes = {
    section: PropTypes.object,
    submissions: PropTypes.array,
    forceEdit: PropTypes.bool,
    printMode: PropTypes.bool,
    callBackEditSubmission: PropTypes.func,
    records: PropTypes.array,
    uuidResearcher: PropTypes.string,
    idSubmission: PropTypes.number,
    updatedAt: PropTypes.string,
    onPrintSubmission: PropTypes.func,
};

export default withLocalize(ShowRecordsSection)